import { Component, ElementRef, ViewChild } from "@angular/core";
import { BoxSize } from "app/model/entities/entity-model";
import { DialogBaseComponent, DialogParams } from "app/shared/dialog-base.component";
import { DomainService } from "app/shared/domain.service";
import { UtilFns } from "app/shared/util-fns";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";

export interface EditBoxSizeDialogParams extends DialogParams {
  boxSize?: BoxSize;
}

@Component({
  templateUrl: "./edit-box-size-dialog.component.html",
  standalone: false,
})
export class EditBoxSizeDialogComponent extends DialogBaseComponent<EditBoxSizeDialogParams> {
  @ViewChild("firstInput") firstInput: ElementRef<HTMLInputElement>;

  boxSize!: BoxSize;
  isAdding = false;

  constructor(
    public config: DynamicDialogConfig<EditBoxSizeDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      width: "500px",
      height: "auto",
    });
    this.prepare();
  }

  private async prepare() {
    this.boxSize = this.params.boxSize;
    if (this.boxSize == null) {
      this.isAdding = true;
      this.boxSize = this.uow.createEntity(BoxSize, {});
      this.boxSize.entityAspect.setUnchanged();
    }

    this.config.header = this.isAdding ? "Add Box Size" : "Edit Box Size";
    this.isPageReady = true;

    await UtilFns.wait(1);
    this.firstInput?.nativeElement?.focus();
  }

  async ok() {
    const ok = await this.save();
    if (!ok) return;
    this.dialogRef.close(true);
  }

  async save() {
    if (this.isAdding) {
      this.boxSize.entityAspect.setAdded();
    }

    const isValid = await this.validate();
    if (!isValid) return false;

    try {
      await this.dbSaveService.saveSelectedChanges([this.boxSize]);
      return true;
    } catch (e) {
      return false;
    }
  }

  async validate() {
    const matchingEntity = await this.dbQueryService.getMatchingEntity(BoxSize, this.boxSize, ["name"]);
    if (matchingEntity != null) {
      await this.dialogService.showOkMessage("Duplicate Name", "A box size with this name already exists.");
      return false;
    }

    const ea = this.boxSize.entityAspect;
    if (!ea.validateEntity()) {
      const vErrors = ea.getValidationErrors();
      const errMsg = vErrors.map((ve) => ve.errorMessage).join("<br>");
      await this.dialogService.showOkMessage("Validation Error(s)", errMsg);
      return false;
    }

    return true;
  }

  async cancel() {
    this.isPageReady = false;
    if (this.isAdding) {
      this.boxSize.entityAspect.setDetached();
    } else {
      this.boxSize.entityAspect?.rejectChanges();
    }
    this.dialogRef.close(false);
  }
}
